/**
 * Main application component
 *
 * Sets up routing, authentication, and user context for the Echo application.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/hooks';
import './App.css';
import { EchoUserProvider } from './hooks/useEchoUser';
import { AppLayout } from './components/AppLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TranscribePage } from './pages/TranscribePage';
import { UploadPage } from './pages/UploadPage';
import { ViewTranscriptionsPage } from './pages/ViewTranscriptionsPage';
import { WorkflowHistoryPage } from './pages/WorkflowHistoryPage';
import { MatterManagerPage } from './pages/MatterManagerPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { BillingPage } from './pages/BillingPage';
import { GitHubMonitorPage } from './pages/GitHubMonitorPage';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './auth/logging';
import {
  Button,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
  textStyles,
  fontSize,
} from '@fti/design-system';

/**
 * Welcome Page Component
 * Displays sign-in UI for unauthenticated users and welcome message for authenticated users
 */
function WelcomePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  // Log authentication errors when they occur
  React.useEffect(() => {
    if (auth.error) {
      const correlationId = getStoredCorrelationId() || generateCorrelationId();
      logAuthEvent('auth_error', 'Authentication error occurred', {
        correlationId,
        errorType: auth.error.name,
        error: auth.error
      });
    }
  }, [auth.error]);

  // Handle sign in with logging
  const handleSignIn = () => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();
    logAuthEvent('auth_attempt', 'User initiated sign-in flow', {
      correlationId
    });
    auth.signinRedirect();
  };

  // Show sign-in card for unauthenticated users
  if (!auth.isAuthenticated && !auth.isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: '60vh' }}
      >
        <Card variant="elevated" style={{ maxWidth: '400px', width: '100%' }}>
          <Card.Body>
            <Stack gap="md">
              <h2 style={{ margin: 0, color: textColor }}>
                Welcome to FTI Echo
              </h2>
              <p style={{ margin: 0, color: mutedColor }}>
                Sign in to access transcription and summarization workflows.
              </p>

              {auth.error && (
                <div
                  style={{
                    fontSize: fontSize.sm,
                    color: errorColor,
                    background: `${theme.colors.semantic.error.light}20`,
                    border: `1px solid ${theme.colors.semantic.error.light}`,
                    borderRadius: '6px',
                    padding: spacing.sm,
                  }}
                >
                  {auth.error.message?.includes('redirect_uri') ? (
                    <>
                      <strong>OAuth Callback URL Error:</strong> The redirect URI is not configured.
                    </>
                  ) : (
                    auth.error.message || 'Authentication failed'
                  )}
                </div>
              )}

              <Button
                variant="solid"
                color="primary"
                onClick={handleSignIn}
              >
                Sign In
              </Button>
            </Stack>
          </Card.Body>
        </Card>
      </Flex>
    );
  }

  // Show loading state
  if (auth.isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: '60vh' }}
      >
        <p style={{ color: mutedColor }}>Checking session...</p>
      </Flex>
    );
  }

  // Show welcome content for authenticated users
  return (
    <Stack gap="lg">
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <h2 style={{ margin: 0, color: textColor }}>
              Welcome to FTI Echo
            </h2>
            <p style={{ margin: 0, color: mutedColor }}>
              Transcription and summarization workflow management for video streams and audio files.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.md,
                marginTop: spacing.sm,
              }}
            >
              <Card
                variant="outlined"
                style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
                onClick={() => navigate('/transcribe')}
              >
                <Card.Body>
                  <Stack gap="xs">
                    <h3 style={{ margin: 0, fontSize: fontSize.base, color: textColor }}>
                      🎙️ Transcribe and Summarize from URL
                    </h3>
                    <p style={{ margin: 0, fontSize: fontSize.sm, color: mutedColor }}>
                      Process streams from Kick or Twitch, videos from YouTube, or podcast episodes via Apple Podcasts
                    </p>
                  </Stack>
                </Card.Body>
              </Card>

              <Card
                variant="outlined"
                style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
                onClick={() => navigate('/upload')}
              >
                <Card.Body>
                  <Stack gap="xs">
                    <h3 style={{ margin: 0, fontSize: fontSize.base, color: textColor }}>
                      📤 Transcribe and Summarize from .mp3 File
                    </h3>
                    <p style={{ margin: 0, fontSize: fontSize.sm, color: mutedColor }}>
                      Upload MP3 files for transcription and summarization
                    </p>
                  </Stack>
                </Card.Body>
              </Card>

              <Card
                variant="outlined"
                style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
                onClick={() => navigate('/view')}
              >
                <Card.Body>
                  <Stack gap="xs">
                    <h3 style={{ margin: 0, fontSize: fontSize.base, color: textColor }}>
                      📄 View Results
                    </h3>
                    <p style={{ margin: 0, fontSize: fontSize.sm, color: mutedColor }}>
                      Browse completed transcriptions and summaries. Play back audio with clickable time stamps in summaries.
                    </p>
                  </Stack>
                </Card.Body>
              </Card>

              <Card
                variant="outlined"
                style={{ cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }}
                onClick={() => navigate('/history')}
              >
                <Card.Body>
                  <Stack gap="xs">
                    <h3 style={{ margin: 0, fontSize: fontSize.base, color: textColor }}>
                      📋 Workflow History
                    </h3>
                    <p style={{ margin: 0, fontSize: fontSize.sm, color: mutedColor }}>
                      Track your submitted workflows and their status
                    </p>
                  </Stack>
                </Card.Body>
              </Card>
            </div>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

/**
 * App Root Component
 * Wraps the application with routing and context providers
 */
export const App: React.FC = () => {
  const { setTheme } = useTheme();

  // Force light theme regardless of system preference
  React.useEffect(() => {
    setTheme('light');
  }, [setTheme]);

  return (
    <BrowserRouter>
      <EchoUserProvider>
        <Routes>
          <Route element={<AppLayout />}>
            {/* Public route - Welcome/Sign In */}
            <Route path="/" element={<WelcomePage />} />

            {/* Protected user routes */}
            <Route
              path="/transcribe"
              element={
                <ProtectedRoute>
                  <TranscribePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <UploadPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/view"
              element={
                <ProtectedRoute>
                  <ViewTranscriptionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <WorkflowHistoryPage />
                </ProtectedRoute>
              }
            />

            {/* Protected admin routes */}
            <Route
              path="/matters"
              element={
                <ProtectedRoute requireAdmin>
                  <MatterManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute requireAdmin>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/github-monitor"
              element={
                <ProtectedRoute requireAdmin>
                  <GitHubMonitorPage />
                </ProtectedRoute>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </EchoUserProvider>
    </BrowserRouter>
  );
};
